import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FormInput } from '../components/FormControls.jsx';
import {
  IntegrationRequirementsPanel,
  integrationStatusBadge,
} from '../components/IntegrationRequirementsPanel.jsx';
import { Modal } from '../components/Modal.jsx';
import { StripePagesSection } from '../components/StripePagesSection.jsx';
import { OrganizationIntegrationsSettings } from '../components/OrganizationIntegrationsSettings.jsx';
import { useAuth } from '../context/AuthContext.jsx';

/** Maps integration provider id → user capability key (matches plan features). */
const PROVIDER_CAPABILITY = {
  gohighlevel: 'ghl',
  microsoft_graph: 'teams',
  bitwarden: 'bitwarden',
  stripe: 'stripe',
  quickbooks: 'quickbooks',
  openai: 'openai',
  figma: 'figma',
  google_analytics: 'google_analytics',
  se_ranking: 'se_ranking',
  hosting: 'hosting',
  pm2: 'pm2',
  docker: 'docker',
};

function integrationPingPath(providerId) {
  if (providerId === 'microsoft_graph') {
    return '/api/integrations/microsoft-graph/ping';
  }

  return `/api/integrations/${providerId}/ping`;
}

const PROVIDERS = [
  {
    id: 'gohighlevel',
    title: 'Go High Level',
    fields: [
      { key: 'token', label: 'Private integration token', secret: true, hint: 'Agency PIT for locations and team sync. Optional if OAuth is connected.' },
      { key: 'oauth_client_id', label: 'Marketplace OAuth Client ID' },
      { key: 'oauth_client_secret', label: 'Marketplace OAuth Client secret', secret: true },
      { key: 'base_url', label: 'API base URL' },
      { key: 'api_version', label: 'API version' },
      { key: 'company_id', label: 'Company ID (optional)', hint: 'Agency Settings → Company (NOT the Location ID from a sub-account URL). Leave blank to auto-detect.' },
    ],
    description:
      'Agency Private Integration token for locations and users. Marketplace OAuth (Agency install) for pipelines and opportunities.',
  },
  {
    id: 'microsoft_graph',
    title: 'Microsoft Teams / SharePoint',
    description: 'Graph app credentials for SharePoint and Outlook. Team members read and reply from their own mailbox inside the portal. The app needs Mail.Read, Mail.ReadWrite, and Mail.Send with admin consent. The shared mailbox below is only what clients can read.',
    fields: [
      { key: 'tenant_id', label: 'Tenant ID' },
      { key: 'client_id', label: 'Application (client) ID' },
      { key: 'client_secret', label: 'Client secret', secret: true },
      { key: 'scope', label: 'Scope' },
      { key: 'graph_base_url', label: 'Graph API base URL' },
      { key: 'projects_drive_id', label: 'SharePoint drive ID for folders' },
      {
        key: 'mailbox_upn',
        label: 'Shared mailbox UPN (client portal)',
        hint: 'Clients see this mailbox. Team members open their own Outlook address, the email on their team profile.',
      },
    ],
  },
  {
    id: 'bitwarden',
    title: 'Bitwarden',
    description: 'Organization API and optional vault CLI credentials for collections.',
    fields: [
      { key: 'identity_url', label: 'Identity URL' },
      { key: 'api_base_url', label: 'API base URL' },
      { key: 'client_id', label: 'Public API client ID (organization.{uuid})' },
      { key: 'client_secret', label: 'Public API client secret', secret: true },
      { key: 'scope', label: 'Scope (use api.organization)' },
      {
        key: 'organization_id',
        label: 'Bitwarden org ID',
        hint: 'UUID from Admin Console → Settings → Organization info. Required to auto-create collections.',
      },
      {
        key: 'cli_client_id',
        label: 'Personal API client ID (user.{uuid})',
        hint: 'Optional — from account Settings → Security → API key. Used with master password for vault CLI.',
      },
      { key: 'cli_client_secret', label: 'Personal API client secret', secret: true },
      {
        key: 'master_password',
        label: 'Vault master password',
        secret: true,
        hint: 'Optional — stored per agency; not required to link existing collections.',
      },
    ],
  },
  {
    id: 'stripe',
    title: 'Stripe',
    description: 'Your Stripe account keys for subscription billing.',
    fields: [
      { key: 'secret_key', label: 'Secret key', secret: true },
      { key: 'webhook_secret', label: 'Webhook signing secret', secret: true },
    ],
  },
  {
    id: 'quickbooks',
    title: 'QuickBooks Online',
    description:
      'OAuth app credentials for time sync. Connect from Time tracker after saving Client ID and Secret.',
    fields: [
      { key: 'client_id', label: 'Client ID' },
      { key: 'client_secret', label: 'Client secret', secret: true },
    ],
  },
  {
    id: 'openai',
    title: 'OpenAI',
    description: 'API key for AI chat assistant when staff is unavailable.',
    fields: [
      { key: 'api_key', label: 'API key', secret: true },
      { key: 'model', label: 'Model (e.g. gpt-4o-mini)' },
    ],
  },
  {
    id: 'figma',
    title: 'Figma',
    description: 'Personal access token for file metadata on project Tools tabs.',
    fields: [{ key: 'api_key', label: 'Personal access token', secret: true }],
  },
  {
    id: 'google_analytics',
    title: 'Google Analytics',
    description: 'GA4 Data API — service account JSON and default property ID.',
    fields: [
      { key: 'property_id', label: 'Default property ID' },
      { key: 'credentials_json', label: 'Service account JSON', secret: true },
    ],
  },
  {
    id: 'se_ranking',
    title: 'SE Ranking',
    description: 'API token for visibility stats on project Tools tabs.',
    fields: [
      { key: 'api_key', label: 'API token', secret: true },
      { key: 'base_url', label: 'API base URL' },
    ],
  },
  {
    id: 'hosting',
    title: 'Hosting monitor',
    description: 'HTTP health checks for project hosting URLs.',
    fields: [{ key: 'timeout_seconds', label: 'Timeout (seconds)' }],
  },
  {
    id: 'pm2',
    title: 'PM2',
    description:
      'API key from api.pm2 (`npm run generate-key`). Enables process control and live WebSocket metrics. Use the plaintext key — not ENCRYPTION_SECRET or API_KEY_ENCRYPTED. Restart api.pm2 after regenerating.',
    fields: [
      { key: 'base_url', label: 'API base URL', hint: 'REST API, e.g. https://pm2.example.com' },
      {
        key: 'ws_url',
        label: 'WebSocket metrics URL',
        hint: 'Realtime stream, e.g. wss://pm2.example.com/ws/metrics',
      },
      {
        key: 'api_key',
        label: 'API key',
        secret: true,
        hint: 'Paste the one-line key shown after generate-key (base64url). Avoid trailing spaces/newlines.',
      },
    ],
  },
  {
    id: 'docker',
    title: 'Docker',
    description:
      'API key from api.docker (`npm run generate-key`). Enables container control and live WebSocket metrics. Use the plaintext key — not ENCRYPTION_SECRET or API_KEY_ENCRYPTED. Restart api.docker after regenerating.',
    fields: [
      { key: 'base_url', label: 'API base URL', hint: 'REST API, e.g. https://docker.example.com' },
      {
        key: 'ws_url',
        label: 'WebSocket metrics URL',
        hint: 'Realtime stream, e.g. wss://docker.example.com/ws/metrics',
      },
      {
        key: 'api_key',
        label: 'API key',
        secret: true,
        hint: 'Paste the one-line key shown after generate-key (base64url). Avoid trailing spaces/newlines.',
      },
    ],
  },
];

function IntegrationEditor({ provider, configured, config, onSaved, setPageError, orgId, stripePages }) {
  const [form, setForm] = useState(config || {});
  const [saving, setSaving] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState('');
  const [cardError, setCardError] = useState('');
  const [oauthBusy, setOauthBusy] = useState(false);
  const [oauthStatus, setOauthStatus] = useState(null);

  const oauthConnected = Boolean(config?.oauth_connected);
  const oauthAppReady = Boolean(
    (form.oauth_client_id || config?.oauth_client_id) &&
      (form.oauth_client_secret || config?.oauth_client_secret_set),
  );

  useEffect(() => {
    setForm(config || {});
  }, [config]);

  useEffect(() => {
    if (provider.id !== 'gohighlevel') {
      return;
    }
    void apiJson('/api/integrations/gohighlevel/oauth/status')
      .then(setOauthStatus)
      .catch(() => setOauthStatus(null));
  }, [provider.id, config]);

  const runHealthCheck = useCallback(async () => {
    if (!provider.pingPath) {
      return;
    }
    setHealthLoading(true);
    setHealthError('');
    try {
      const result = await apiJson(provider.pingPath);
      setHealth({ ...result, loading: false });
    } catch (e) {
      setHealth(null);
      const body = e.body && typeof e.body === 'object' ? e.body : null;
      if (body?.requirements?.length) {
        setHealth({ ...body, ok: false, loading: false });
      } else {
        setHealthError(e.message);
      }
    } finally {
      setHealthLoading(false);
    }
  }, [provider.pingPath]);

  useEffect(() => {
    if (configured && provider.pingPath) {
      void runHealthCheck();
    } else {
      setHealth(null);
      setHealthError('');
    }
  }, [configured, provider.id, provider.pingPath, runHealthCheck]);

  const save = async () => {
    setSaving(true);
    setCardError('');
    try {
      const payload = { config: { ...form } };
      for (const field of provider.fields) {
        if (field.secret && form[field.key] === '********') {
          delete payload.config[field.key];
        }
      }
      const res = await apiJson(`/api/integrations/${provider.id}`, {
        method: 'PUT',
        body: payload,
      });
      setForm(res.config || {});
      onSaved();
      void runHealthCheck();
    } catch (e) {
      setCardError(e.message);
      setPageError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const connectGhlOAuth = async () => {
    setOauthBusy(true);
    setCardError('');
    try {
      if (form.oauth_client_id || form.oauth_client_secret || form.token) {
        const payload = { config: { ...form } };
        for (const field of provider.fields) {
          if (field.secret && form[field.key] === '********') {
            delete payload.config[field.key];
          }
        }
        await apiJson(`/api/integrations/${provider.id}`, {
          method: 'PUT',
          body: payload,
        });
        onSaved();
      }
      const { auth_url: authUrl } = await apiJson('/api/integrations/gohighlevel/oauth/connect', {
        method: 'POST',
      });
      window.location.href = authUrl;
    } catch (e) {
      setCardError(e.message);
      setPageError(e.message);
      setOauthBusy(false);
    }
  };

  const disconnectGhlOAuth = async () => {
    setOauthBusy(true);
    setCardError('');
    try {
      await apiJson('/api/integrations/gohighlevel/oauth/disconnect', { method: 'POST' });
      onSaved();
      setOauthStatus({ connected: false });
      void runHealthCheck();
    } catch (e) {
      setCardError(e.message);
      setPageError(e.message);
    } finally {
      setOauthBusy(false);
    }
  };

  return (
    <div className="integration-editor">
      {cardError ? <p className="err">{cardError}</p> : null}

      <div className="stack">
        {provider.fields.map((field) => (
          <label key={field.key} className="field">
            <span>{field.label}</span>
            <FormInput
              type={field.secret ? 'password' : 'text'}
              value={form[field.key] ?? ''}
              placeholder={field.secret && form[`${field.key}_set`] ? '********' : ''}
              onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
            />
            {field.hint ? <span className="muted small">{field.hint}</span> : null}
          </label>
        ))}
      </div>

      {provider.id === 'gohighlevel' ? (
        <>
          <p className="muted small" style={{ marginTop: '0.75rem' }}>
            OAuth redirect URL for your Marketplace app:{' '}
            <code>{`${window.location.origin.replace(':3001', ':8088')}/api/integrations/gohighlevel/callback`}</code>
          </p>
          <p className="muted small">
            Scopes:{' '}
            <code>locations.readonly users.readonly opportunities.readonly oauth.readonly oauth.write companies.readonly</code>
          </p>
          <p className="small" style={{ marginTop: '0.5rem' }}>
            OAuth:{' '}
            <span className={`badge ${oauthConnected || oauthStatus?.connected ? 'text-bg-success' : 'text-bg-secondary'}`}>
              {oauthConnected || oauthStatus?.connected ? 'connected' : 'not connected'}
            </span>
          </p>
          <p className="muted small">
            <Link to="/admin/ghl-locations">GHL Locations →</Link> link sub-accounts to portal clients, sync team members, and set
            pipelines.
          </p>
        </>
      ) : null}

      {provider.id === 'pm2' ? (
        <p className="muted small" style={{ marginTop: '0.75rem' }}>
          <Link to="/admin/pm2">Open PM2 →</Link>
          {' · '}
          <Link to="/admin/pm2/apps">Open npm apps →</Link>
        </p>
      ) : null}

      {provider.id === 'docker' ? (
        <p className="muted small" style={{ marginTop: '0.75rem' }}>
          <Link to="/admin/docker">Open Docker →</Link>
        </p>
      ) : null}

      <div className="row-actions" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save'}
        </button>
        {provider.id === 'gohighlevel' && oauthAppReady && !(oauthConnected || oauthStatus?.connected) ? (
          <button type="button" className="btn btn-outline-primary" disabled={oauthBusy} onClick={() => void connectGhlOAuth()}>
            {oauthBusy ? 'Redirecting…' : 'Connect Go High Level OAuth'}
          </button>
        ) : null}
        {provider.id === 'gohighlevel' && (oauthConnected || oauthStatus?.connected) ? (
          <button type="button" className="btn btn-outline-danger" disabled={oauthBusy} onClick={() => void disconnectGhlOAuth()}>
            {oauthBusy ? 'Disconnecting…' : 'Disconnect OAuth'}
          </button>
        ) : null}
        {provider.pingPath ? (
          <button type="button" className="btn btn-outline-secondary" onClick={() => void runHealthCheck()} disabled={healthLoading}>
            {healthLoading ? 'Checking…' : 'Recheck permissions'}
          </button>
        ) : null}
      </div>

      <IntegrationRequirementsPanel result={health} loading={healthLoading} error={healthError} />

      {provider.id === 'stripe' && orgId ? (
        <p className="muted small" style={{ marginTop: '0.75rem' }}>
          Stripe webhook URL: <code>/api/webhooks/stripe/{orgId}</code>
        </p>
      ) : null}

      {stripePages}
    </div>
  );
}

function IntegrationStatusCell({ configured, health }) {
  const badge = integrationStatusBadge(configured, health);
  return <span className={`badge ${badge.className}`}>{badge.text}</span>;
}

export function AdminIntegrationsPage() {
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [integrations, setIntegrations] = useState(null);
  const [healthById, setHealthById] = useState({});
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const availableProviders = useMemo(
    () => PROVIDERS.filter((p) => caps[PROVIDER_CAPABILITY[p.id]]).map((p) => ({ ...p, pingPath: integrationPingPath(p.id) })),
    [caps],
  );

  const activeProvider = useMemo(
    () => availableProviders.find((p) => p.id === activeId) || null,
    [availableProviders, activeId],
  );

  useEffect(() => {
    if (searchParams.get('ghl_connected') === '1') {
      setFlash('Go High Level OAuth connected successfully.');
      setActiveId('gohighlevel');
      searchParams.delete('ghl_connected');
      setSearchParams(searchParams, { replace: true });
    }
    const ghlError = searchParams.get('ghl_error');
    if (ghlError) {
      setError(decodeURIComponent(ghlError));
      setActiveId('gohighlevel');
      searchParams.delete('ghl_error');
      setSearchParams(searchParams, { replace: true });
    }
    const openProvider = searchParams.get('provider');
    if (openProvider && PROVIDERS.some((p) => p.id === openProvider)) {
      setActiveId(openProvider);
      searchParams.delete('provider');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const listData = await apiJson('/api/integrations');
      setIntegrations(listData.integrations);
      setOrgId(listData.organization_id);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!integrations || availableProviders.length === 0) {
      return;
    }

    let cancelled = false;
    const run = async () => {
      const next = {};
      await Promise.all(
        availableProviders.map(async (provider) => {
          const configured = Boolean(integrations[provider.id]?.configured);
          if (!configured || !provider.pingPath) {
            next[provider.id] = configured ? { ok: true } : null;
            return;
          }
          try {
            next[provider.id] = await apiJson(provider.pingPath);
          } catch (e) {
            const body = e.body && typeof e.body === 'object' ? e.body : null;
            next[provider.id] = body?.requirements ? { ...body, ok: false } : { ok: false, message: e.message };
          }
        }),
      );
      if (!cancelled) {
        setHealthById(next);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [integrations, availableProviders]);

  const closeModal = () => setActiveId(null);

  return (
    <div>
      <p>
        <Link to="/admin">← Admin</Link>
      </p>
      <h2 style={{ marginTop: 0 }} data-tour="page-title">
        Integrations
      </h2>
      <p className="muted small">
        Connect third-party APIs for your agency. Click a row to configure. Available integrations depend on your subscription plan.
      </p>
      {loading ? <p className="muted">Loading…</p> : null}
      {flash ? <p className="text-success">{flash}</p> : null}
      {error ? <p className="err">{error}</p> : null}

      {integrations ? (
        <div className="table-wrap" data-tour="integrations-list">
          <table className="data-table integrations-table">
            <thead>
              <tr>
                <th>Integration</th>
                <th>Status</th>
                <th>Summary</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {availableProviders.length === 0 ? (
                <tr>
                  <td colSpan={4} className="muted">
                    No integration providers are included in your current plan.
                  </td>
                </tr>
              ) : null}
              {availableProviders.map((provider) => {
                const configured = Boolean(integrations[provider.id]?.configured);
                const health = healthById[provider.id];
                return (
                  <tr
                    key={provider.id}
                    className="integrations-table-row"
                    tabIndex={0}
                    onClick={() => setActiveId(provider.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setActiveId(provider.id);
                      }
                    }}
                  >
                    <td>
                      <strong>{provider.title}</strong>
                    </td>
                    <td>
                      <IntegrationStatusCell configured={configured} health={health} />
                    </td>
                    <td className="muted small integrations-table-summary">{provider.description || '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveId(provider.id);
                        }}
                      >
                        Configure
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      <Modal
        open={Boolean(activeProvider && integrations)}
        title={activeProvider?.title || 'Integration'}
        description={activeProvider?.description}
        onClose={closeModal}
        size="lg"
      >
        {activeProvider && integrations ? (
          <IntegrationEditor
            key={activeProvider.id}
            provider={activeProvider}
            configured={integrations[activeProvider.id]?.configured}
            config={integrations[activeProvider.id]?.config}
            orgId={orgId}
            onSaved={load}
            setPageError={setError}
            stripePages={
              activeProvider.id === 'stripe' ? (
                <StripePagesSection stripeConfigured={integrations.stripe?.configured} />
              ) : null
            }
          />
        ) : null}
      </Modal>

      <div style={{ marginTop: '1.5rem' }} data-tour="integrations-routing">
        <h3>Webhooks &amp; routing</h3>
        <p className="muted small">
          Configure Teams notifications, asset upload routing, and the Go High Level inbound webhook.
        </p>
        <OrganizationIntegrationsSettings />
      </div>
    </div>
  );
}
